import { useState, useEffect, useRef } from 'react';
import { Bell, Megaphone, BookOpen, Clock, CheckCircle, MessageSquare, TrendingDown } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useNotifications, useMarkAllRead } from '@/lib/hooks/useNotifications';
import type { Notification, NotificationType } from '@/lib/api/notifications';

interface NotificationBellProps { unreadCount?: number; }

const TYPE_ICON: Record<NotificationType, React.ElementType> = {
  announcement: Megaphone,
  assignment_created: BookOpen,
  session_reminder: Clock,
  feedback: CheckCircle,
  trainer_message: MessageSquare,
  review_digest: TrendingDown,
};

const TYPE_COLOR: Record<NotificationType, string> = {
  announcement: 'bg-indigo-500/15 text-indigo-400',
  assignment_created: 'bg-yellow-500/15 text-yellow-400',
  session_reminder: 'bg-green-500/15 text-green-400',
  feedback: 'bg-pink-500/15 text-pink-400',
  trainer_message: 'bg-blue-500/15 text-blue-400',
  review_digest: 'bg-rose-500/15 text-rose-400',
};

// Fallbacks for a type this build has never heard of.
//
// The API and this app deploy independently, so the server can start sending a
// new kind of notification before the client knows about it. Indexing the maps
// directly then yields `undefined`, and rendering `undefined` as a component
// throws — which took out the whole notification panel, not just the one row.
// An unknown notification should look plain, not break the bell.
const FALLBACK_ICON = Bell;
const FALLBACK_COLOR = 'bg-muted text-muted-foreground';

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'Yesterday';
  return `${Math.floor(hours / 24)}d ago`;
}

function NotificationItem({ n, onNavigate }: { n: Notification; onNavigate: (link: string) => void }) {
  const Icon = TYPE_ICON[n.type] ?? FALLBACK_ICON;
  return (
    <button
      type="button"
      disabled={!n.link}
      onClick={() => n.link && onNavigate(n.link)}
      className={`w-full text-left flex gap-3 px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors disabled:cursor-default ${n.read ? 'opacity-60' : 'bg-primary/[0.03]'}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${TYPE_COLOR[n.type] ?? FALLBACK_COLOR}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-sm font-semibold text-foreground">{n.title}</span>
          {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
        </div>
        <p className="text-xs text-muted-foreground leading-snug">{n.body}</p>
        <span className="text-[11px] text-muted-foreground/60 mt-1 block">{relativeTime(n.created_at)}</span>
      </div>
    </button>
  );
}

export function NotificationBell({ unreadCount = 0 }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: notifications = [], isPending } = useNotifications(open);
  const { mutate: markAllRead } = useMarkAllRead();

  const hasMarkedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) {
      hasMarkedRef.current = false;
      return;
    }
    if (hasMarkedRef.current) return;
    // Mark every unread read server-side (not just the fetched page), so the
    // bell stops ringing even when the unread count exceeds the loaded list.
    if (unreadCount > 0) {
      markAllRead();
      hasMarkedRef.current = true;
    }
  }, [open, unreadCount, markAllRead]);

  const handleNavigate = (link: string) => {
    setOpen(false);
    navigate({ href: link });
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground hover:text-foreground"
      >
        <Bell className={`h-4 w-4 ${unreadCount > 0 ? 'animate-bell-ring' : ''}`} />
        {unreadCount > 0 && (
          <>
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500 animate-ping opacity-75" />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full border border-card bg-red-500" />
          </>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-80 rounded-xl border border-border bg-popover shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="text-sm font-semibold">Notifications</span>
            {(unreadCount > 0 || notifications.some((n) => !n.read)) && (
              <button
                type="button"
                onClick={() => markAllRead()}
                className="text-xs text-primary hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {isPending && (
              <div className="py-10 text-center text-xs text-muted-foreground">Loading…</div>
            )}
            {!isPending && notifications.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <div className="mb-3 rounded-full bg-muted p-3">
                  <Bell className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-semibold text-foreground">No notifications yet</p>
                <p className="mt-1 text-xs text-muted-foreground">Class reminders, deadlines and feedback will show up here.</p>
              </div>
            )}
            {!isPending && notifications.map((n) => (
              <NotificationItem key={n.id} n={n} onNavigate={handleNavigate} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
